import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Flag } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  formatDateTime,
  formatDayLabel,
  formatOutcome,
  formatPhone,
  formatStep,
  formatTime,
} from '@/lib/conversationFormat';
import { takeoverStateOf } from '@/lib/liveConversation';
import type {
  ContactThread,
  ConversationDetailResponse,
  LiveCallInfo,
} from '@/services/conversationService';
import { useAssistantEnabled } from '@/hooks/useAssistantEnabled';
import { reportProblemPath, ticketFormPath } from '@/lib/assistantLinks';
import { useServerNow } from './useServerNow';
import { ElapsedClock } from './TimeText';

/** Maps an agent number to how the salon calls that line ("Demosalon"). */
export type AgentLabel = (agentNumber: string | null | undefined) => string | null;

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-b border-border/70 px-5 py-4 last:border-b-0">
      <h3 className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5 text-[13px]">
      <dt className="flex-shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right font-medium text-foreground [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

function PhoneLink({ phone }: { phone: string }) {
  const display = formatPhone(phone);
  if (!display) return null;
  return (
    <a
      href={`tel:${display}`}
      className="underline decoration-border underline-offset-2 hover:decoration-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      {display}
    </a>
  );
}

/**
 * "Problem melden": where the assistant is switched on, it looks at the
 * conversation first and leads to the ticket only if needed; everywhere else
 * (and until the server has said so) it is the ticket form, as before.
 */
export function ReportLink({ conversationId }: { conversationId: number }) {
  const { enabled } = useAssistantEnabled();
  return (
    <div className="px-5 py-4">
      <Link
        to={enabled ? reportProblemPath(conversationId) : ticketFormPath(conversationId)}
        className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-brand/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        title={
          enabled
            ? 'Der Agent hat hier etwas falsch gemacht? Der Assistent prüft dieses Gespräch mit dir – bei Bedarf folgt ein Support-Ticket.'
            : 'Der Agent hat hier etwas falsch gemacht? Support-Ticket mit diesem Gespräch anlegen.'
        }
      >
        <Flag className="h-3.5 w-3.5" aria-hidden="true" />
        Problem melden
      </Link>
    </div>
  );
}

function CustomerBlock({
  name,
  phone,
  agentNumber,
  agentLabel,
}: {
  name: string | null;
  phone: string;
  agentNumber: string | null | undefined;
  agentLabel?: AgentLabel;
}) {
  const line = agentNumber ? agentLabel?.(agentNumber) ?? formatPhone(agentNumber) : null;
  return (
    <Block title="Kunde">
      <dl>
        <Row label="Name">{name?.trim() || '—'}</Row>
        <Row label="Nummer">
          <PhoneLink phone={phone} />
        </Row>
        <Row label="Über">{line}</Row>
      </dl>
    </Block>
  );
}

const STATE_LABELS = {
  requested: 'Wartet auf Mitarbeiter',
  staff: 'Übernommen',
  callback: 'Wartet auf Rückmeldung',
} as const;

/** Context of a WhatsApp contact: customer, handoff, current session, all sessions. */
export function ContactContext({
  thread,
  me = null,
  agentLabel,
  onJumpToSession,
}: {
  thread: ContactThread;
  me?: string | null;
  agentLabel?: AgentLabel;
  onJumpToSession: (conversationId: number) => void;
}) {
  const now = useServerNow(60_000);
  const takeover = thread.takeover;
  const state = takeoverStateOf(takeover);
  const current = thread.sessions[thread.sessions.length - 1] ?? null;
  const newestFirst = [...thread.sessions].reverse();

  return (
    <div className="flex flex-col">
      <CustomerBlock
        name={thread.customer_name}
        phone={thread.customer_phone}
        agentNumber={thread.agent_number}
        agentLabel={agentLabel}
      />

      {state && takeover && (
        <Block title="Übergabe">
          <dl>
            <Row label="Status">{STATE_LABELS[state]}</Row>
            <Row label="Angefragt">{formatDateTime(takeover.requested_at, now)}</Row>
            <Row label="Rückmeldung seit">{state === 'callback' ? formatDateTime(takeover.callback_at, now) : null}</Row>
            <Row label="Übernommen">
              {state === 'staff'
                ? [
                    formatTime(takeover.started_at),
                    takeover.taken_by && me && takeover.taken_by.toLowerCase() === me.toLowerCase()
                      ? 'von dir'
                      : takeover.taken_by,
                  ]
                    .filter(Boolean)
                    .join(' · ') || null
                : null}
            </Row>
            <Row label="Grund">{takeover.request_reason?.trim() || null}</Row>
          </dl>
          {takeover.request_summary?.trim() && (
            <p className="mt-2 text-[13px] leading-relaxed text-foreground/85">{takeover.request_summary}</p>
          )}
        </Block>
      )}

      {current && (current.summary || current.end_reason_text) && (
        <Block title="Zusammenfassung">
          {current.summary && <p className="text-[13px] leading-relaxed text-foreground/85">{current.summary}</p>}
          {current.end_reason_text && (
            <p className="mt-1.5 text-xs text-muted-foreground">{current.end_reason_text}</p>
          )}
        </Block>
      )}

      {newestFirst.length > 0 && (
        <Block title={thread.has_more ? 'Sitzungen (neueste)' : 'Sitzungen'}>
          <ul className="-mx-2 flex flex-col">
            {newestFirst.map((session) => {
              const outcome = formatOutcome(session.status);
              const isCurrent = session === current;
              return (
                <li key={session.conversation_id}>
                  <button
                    type="button"
                    onClick={() => onJumpToSession(session.conversation_id)}
                    className={cn(
                      'group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="font-medium text-foreground">
                        {formatDayLabel(session.started_at ?? session.last_message_at, now) ?? 'Sitzung'}
                      </span>
                      <span className="text-muted-foreground">
                        {' · '}
                        {outcome ?? (isCurrent ? 'offen' : 'ohne Abschluss')}
                      </span>
                    </span>
                    {session.is_test && (
                      <span className="rounded bg-muted px-1 text-[10px] font-semibold uppercase text-muted-foreground">
                        Test
                      </span>
                    )}
                    <ChevronRight
                      className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                      aria-hidden="true"
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </Block>
      )}

      {current && !current.is_test && <ReportLink conversationId={current.conversation_id} />}
    </div>
  );
}

const CALL_STATUS_LABELS: Record<LiveCallInfo['status'], string> = {
  live: 'läuft',
  transferring: 'wird weitergeleitet',
  ended: 'beendet',
};

/** Context of a running call. */
export function CallContext({ call, agentLabel }: { call: LiveCallInfo; agentLabel?: AgentLabel }) {
  const now = useServerNow(60_000);
  const step = formatStep(call.last_step);
  return (
    <div className="flex flex-col">
      <CustomerBlock
        name={call.customer_name}
        phone={call.customer_phone}
        agentNumber={call.agent_number}
        agentLabel={agentLabel}
      />
      <Block title="Anruf">
        <dl>
          <Row label="Status">{CALL_STATUS_LABELS[call.status]}</Row>
          <Row label="Beginn">{formatDateTime(call.started_at, now)}</Row>
          <Row label="Dauer">
            <ElapsedClock since={call.started_at} until={call.ended_at ?? null} />
          </Row>
          <Row label="Zuletzt">{step}</Row>
        </dl>
      </Block>
    </div>
  );
}

/** Context of one stored conversation (a finished call, or a session without contact). */
export function SessionContext({ detail, agentLabel }: { detail: ConversationDetailResponse; agentLabel?: AgentLabel }) {
  const now = useServerNow(60_000);
  const record = detail.record;
  return (
    <div className="flex flex-col">
      <CustomerBlock
        name={detail.customer_name}
        phone={detail.customer_phone}
        agentNumber={null}
        agentLabel={agentLabel}
      />
      <Block title={record.type === 'phone' ? 'Anruf' : 'Sitzung'}>
        <dl>
          <Row label="Beginn">{formatDateTime(record.started_at ?? record.last_message_at, now)}</Row>
          <Row label="Ende">{record.ended_at ? formatTime(record.ended_at) : null}</Row>
          <Row label="Ergebnis">{formatOutcome(record.status)}</Row>
        </dl>
      </Block>
      {(record.summary || record.end_reason_text) && (
        <Block title="Zusammenfassung">
          {record.summary && <p className="text-[13px] leading-relaxed text-foreground/85">{record.summary}</p>}
          {record.end_reason_text && <p className="mt-1.5 text-xs text-muted-foreground">{record.end_reason_text}</p>}
        </Block>
      )}
      {!record.is_test && <ReportLink conversationId={record.id} />}
    </div>
  );
}
